// Student registration and quiz flow (3 steps: register → quiz → done)
import { useEffect, useState } from 'react'
import { XCircle, CheckCircle2, AlertTriangle } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Button } from '@/ui/button'
import { useClassSessionStore, useCourseStore, getCoursePassingScore, getCourseQuestions } from '@/features/classes/stores'
import { cn } from '@/core/lib/utils'
import { ModalOverlay } from '@/ui/ModalOverlay'

import { inputClass } from '../utils/formatters'


interface StudentJoinModalProps {
  sessionId: string
  onClose: () => void
}

export function StudentJoinModal({ sessionId, onClose }: StudentJoinModalProps) {
  const sessions = useClassSessionStore((s) => s.sessions)
  const registerStudent = useClassSessionStore((s) => s.registerStudent)
  const submitQuiz = useClassSessionStore((s) => s.submitQuiz)
  const courses = useCourseStore((s) => s.courses)
  const loadCourses = useCourseStore((s) => s.loadCourses)

  useEffect(() => {
    loadCourses()
  }, [loadCourses])

  const session = sessions.find((s) => s.id === sessionId)
  const course = session ? courses.find((c) => c.slug === session.courseId) : null
  const passingScore = getCoursePassingScore(course)
  const questions = getCourseQuestions(course)

  const [step, setStep] = useState<'register' | 'quiz' | 'done'>('register')
  const [attendeeId, setAttendeeId] = useState('')
  const [score, setScore] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  // Registration form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [signed, setSigned] = useState(false)
  const [regError, setRegError] = useState('')

  // Quiz state
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [quizError, setQuizError] = useState('')

  if (!session) {
    return (
      <ModalOverlay onClose={onClose}>
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-sm text-red-500">Session not found or not active.</p>
            <Button className="mt-4" onClick={onClose}>Close</Button>
          </CardContent>
        </Card>
      </ModalOverlay>
    )
  }

  const courseName = course?.name ?? 'Training Session'

  async function handleRegister() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!firstName.trim() || !lastName.trim()) {
      setRegError('First and last name are required.')
      return
    }
    if (!emailRegex.test(email.trim())) {
      setRegError('Please enter a valid email address.')
      return
    }
    if (!phone.trim()) {
      setRegError('Phone number is required.')
      return
    }
    if (!signed) {
      setRegError('Please confirm your attendance with the digital signature.')
      return
    }
    setRegError('')
    setSubmitting(true)
    try {
      const { attendee } = await registerStudent(sessionId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        company: company.trim(),
      })
      setAttendeeId(attendee.id)
      setStep(questions.length > 0 ? 'quiz' : 'done')
    } catch {
      setRegError('Error registering. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleQuizSubmit() {
    const unanswered = questions.filter((q) => answers[q.id] === undefined)
    if (unanswered.length > 0) {
      setQuizError(`Please answer all questions (${unanswered.length} unanswered).`)
      return
    }
    setQuizError('')
    setSubmitting(true)
    try {
      const s = await submitQuiz(attendeeId, answers)
      setScore(s)
      setStep('done')
    } catch {
      setQuizError('Error submitting the diagnostic. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-navy">
            {step === 'register' && 'Student Registration'}
            {step === 'quiz' && `Diagnostic – ${courseName}`}
            {step === 'done' && 'Registration Completed'}
          </CardTitle>
          <CardDescription>
            {step === 'register' && `${courseName} · ${session.locationName ?? session.location ?? '—'}`}
            {step === 'quiz' && `${questions.length} preguntas · Ayuda a personalizar la sesión`}
            {step === 'done' && 'Your results have been recorded.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: Registration form */}
          {step === 'register' && (
            <div className="space-y-4">
              {regError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <XCircle className="w-4 h-4 shrink-0" />
                  {regError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-navy mb-1">First Name *</label>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Juan" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-1">Last Name *</label>
                  <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="García" className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-1">Email *</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="juan@empresa.com" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-1">Phone *</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+52 (55) 0000-0000" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-1">
                  Company <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" className={inputClass} />
              </div>
              <div className="border border-slate-150 rounded-lg p-4 bg-gray-50">
                <p className="text-sm font-medium text-navy mb-3">Digital Signature</p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} className="mt-0.5 accent-primary" />
<span className="text-sm text-slate-500">
                      I confirm my attendance and agree to participate in this training session.
                      I understand my information will be used to generate my competency certificate.
                    </span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={handleRegister} disabled={submitting}>
                  {submitting ? 'Registering…' : 'Next: Diagnostic'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Quiz */}
          {step === 'quiz' && (
            <div className="space-y-5">
              {quizError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <XCircle className="w-4 h-4 shrink-0" />
                  {quizError}
                </div>
              )}
              {questions.map((q, qi) => (
                <div key={q.id} className="space-y-2">
                  <p className="text-sm font-medium text-navy">{qi + 1}. {q.text}</p>
                  <div className="space-y-1.5 pl-3">
                    {q.options.map((opt, oi) => (
                      <label
                        key={`${q.id}-${opt}`}
                        className={cn(
                          'flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors text-sm',
                          answers[q.id] === oi
                            ? 'border-primary bg-orange-50 text-navy'
                            : 'border-slate-150 text-slate-500 hover:border-primary hover:bg-orange-50'
                        )}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={oi}
                          checked={answers[q.id] === oi}
                          onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: oi }))}
                          className="accent-primary"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button onClick={handleQuizSubmit} disabled={submitting}>
                  <CheckCircle2 className="w-4 h-4" />
                  {submitting ? 'Sending…' : 'Submit Diagnostic'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Completion summary */}
          {step === 'done' && (
            <div className="text-center space-y-4 py-4">
              <div className={cn('w-16 h-16 rounded-full flex items-center justify-center mx-auto', score >= passingScore ? 'bg-green-100' : 'bg-yellow-100')}>
                {score >= passingScore ? (
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-yellow-600" />
                )}
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{score}%</p>
                <p className="text-sm text-slate-500 mt-1">
{score >= passingScore
                      ? 'Excellent score! You are well prepared for this course.'
                      : 'There are some topics to review during today\'s session.'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-left space-y-1">
                <p className="font-medium text-navy">What\'s next?</p>
                <p className="text-slate-500">
                  Your registration is confirmed. Attend the class and you will receive your certificate
                  once the session ends.
                </p>
              </div>
              <Button onClick={onClose} className="w-full">Done</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </ModalOverlay>
  )
}
